import { useState } from 'react';
import { toApiError } from '../api/client';
import { billingApi } from '../api/endpoints';
import type { ListingPlan, Payment, PaymentOrder, PaymentRequirement } from '../api/types';
import { date, money } from '../utils/format';
import { openRazorpayCheckout } from '../utils/razorpay-checkout';
import { IconAlert, IconCard, IconCheck, IconClock } from './Icons';
import { InlineError } from './States';

/**
 * The purchase flow for a branch subscription or a single listing fee.
 *
 * Three things it deliberately does not do. It never computes a price: every amount shown
 * comes from the order the server created, so what the owner approves is what the server
 * recorded. It never marks anything paid: the success screen appears only after the API
 * returns a confirmed payment. And it never decides whether simulation is allowed - that
 * comes from `requirement.simulatedCheckoutAvailable`, which the server derives from its
 * own environment and provider.
 */

type Stage = 'select' | 'summary' | 'pending' | 'done';
const paymentConfirmationTimeoutMs = 90_000;
const paymentConfirmationIntervalMs = 3_000;

export function Checkout({
  propertyId,
  propertyName,
  requirement,
  plans,
  onActivated,
  onCancel,
}: {
  propertyId: string;
  propertyName: string;
  requirement: PaymentRequirement;
  plans: ListingPlan[];
  /** Called after a confirmed payment so the caller can re-read server state. */
  onActivated: () => Promise<void> | void;
  onCancel?: () => void;
}) {
  const [stage, setStage] = useState<Stage>('select');
  const [planId, setPlanId] = useState(plans.find((p) => p.isDefault)?.id ?? plans[0]?.id ?? '');
  const [order, setOrder] = useState<PaymentOrder | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plan = plans.find((p) => p.id === planId);

  // The server is the only thing that may say a simulated confirmation is possible.
  const canSimulate = requirement.simulatedCheckoutAvailable;
  const canUseRazorpay = requirement.gatewayIntegrated && order?.provider === 'Razorpay';

  const createOrder = async () => {
    if (!planId || busy) return; // `busy` is what stops a double submit creating two orders
    setBusy(true);
    setError(null);

    try {
      setOrder(await billingApi.createOrder(propertyId, planId));
      setStage('summary');
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  /** Dismisses the receipt and only then re-reads server state. */
  const finish = async () => {
    if (busy) return;
    setBusy(true);

    try {
      await onActivated();
      onCancel?.();
    } finally {
      setBusy(false);
    }
  };

  const confirmSimulated = async () => {
    if (!order || busy) return;
    setBusy(true);
    setError(null);

    try {
      // Idempotent server-side: confirming an already-paid payment returns the same result
      // rather than granting a second subscription period.
      const result = await billingApi.simulatePayment(order.paymentId, true);
      setPayment(result);

      if (result.status === 'Paid') {
        // Deliberately not refreshing here. onActivated() reloads the listing, and the
        // page renders a skeleton while it does, which unmounts this component and takes
        // the receipt with it. The refresh happens when the owner dismisses the success
        // screen instead, so they return to a page that already reflects the payment.
        setStage('done');
      } else {
        setError('The payment did not complete. Its status is ' + result.status + '.');
      }
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmRazorpay = async (result: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) => {
    if (!order) return;
    setBusy(true);
    setError(null);
    setStage('pending');

    try {
      await billingApi.confirmCheckout({
        paymentId: order.paymentId,
        providerOrderId: result.razorpay_order_id,
        providerPaymentId: result.razorpay_payment_id,
        signature: result.razorpay_signature,
      });

      const deadline = Date.now() + paymentConfirmationTimeoutMs;
      while (Date.now() < deadline) {
        const payments = await billingApi.payments({ propertyId, page: 1, pageSize: 50 });
        const latest = payments.items.find((item) => item.id === order.paymentId);
        if (latest?.status === 'Paid') {
          await onActivated();
          return;
        }
        if (latest?.status === 'Failed') {
          setStage('summary');
          setError('Razorpay reported that this payment failed. You can try again from the order summary.');
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, paymentConfirmationIntervalMs));
      }
      setError('Payment is still being verified by RoomKart. Keep this page open and check the status again shortly; do not pay again.');
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const startRazorpayCheckout = async () => {
    if (!order || busy) return;
    setBusy(true);
    setError(null);

    try {
      await openRazorpayCheckout(order.checkoutPayload, {
        onSuccess: (result) => { void confirmRazorpay(result); },
        onFailure: (message) => {
          setError(message);
          setBusy(false);
        },
        onDismiss: () => setBusy(false),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Razorpay Checkout could not be opened.');
      setBusy(false);
    }
  };

  const checkPaymentStatus = async () => {
    if (!order || busy) return;
    setBusy(true);
    setError(null);

    try {
      const payments = await billingApi.payments({ propertyId, page: 1, pageSize: 50 });
      const latest = payments.items.find((item) => item.id === order.paymentId);
      if (latest?.status === 'Paid') {
        await onActivated();
      } else if (latest?.status === 'Failed') {
        setError('Razorpay reported that this payment failed. You can try again from the order summary.');
        setStage('summary');
      } else {
        setError('Payment is still awaiting Razorpay confirmation. Check again shortly; do not create another order.');
      }
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  // --- Success -------------------------------------------------------------------------

  if (stage === 'done' && payment) {
    return (
      <div className="sf-card sf-card-pad">
        <div className="sf-checkout__done">
          <span className="sf-checkout__tick" aria-hidden="true">
            <IconCheck />
          </span>
          <h3 className="h5 mb-1">
            {payment.isSimulated ? 'Development payment successful' : 'Payment successful'}
          </h3>
          <p className="sf-muted mb-0">{propertyName}</p>
        </div>

        {payment.isSimulated && (
          <div className="sf-simulated-banner my-3" role="note">
            <strong>No money was charged.</strong> This payment was completed by the development
            simulator and is recorded as simulated. It is not a real financial transaction.
          </div>
        )}

        <dl className="sf-kv mb-3">
          <div>
            <dt>Reference</dt>
            <dd className="font-monospace">{payment.id}</dd>
          </div>
          <div>
            <dt>Amount</dt>
            <dd>{money(payment.amount)}</dd>
          </div>
          <div>
            <dt>Tax</dt>
            <dd>{money(payment.taxAmount)}</dd>
          </div>
          <div>
            <dt>Total paid</dt>
            <dd>
              <strong>{money(payment.totalAmount)}</strong>
            </dd>
          </div>
          {payment.paidAtUtc && (
            <div>
              <dt>Activated</dt>
              <dd>{date(payment.paidAtUtc)}</dd>
            </div>
          )}
        </dl>

        <button type="button" className="btn btn-primary" onClick={finish} disabled={busy}>
          {busy ? 'Refreshing…' : 'Back to billing'}
        </button>
      </div>
    );
  }

  if (stage === 'pending' && order) {
    return (
      <div className="sf-card sf-card-pad">
        <h3 className="sf-section-title">Confirming your payment</h3>
        <InlineError message={error} />
        <p role="status" aria-live="polite">
          {busy ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
              Razorpay returned a success response. RoomKart is securely confirming your payment.
              This usually takes a few seconds. Keep this page open and do not pay again.
            </>
          ) : (
            'RoomKart is still waiting for Razorpay to verify this payment. Do not pay again while this order is pending.'
          )}
        </p>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-primary" onClick={checkPaymentStatus} disabled={busy}>
            {busy ? 'Checking…' : 'Check payment status'}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setStage('summary')} disabled={busy}>
            Back to order
          </button>
        </div>
      </div>
    );
  }

  // --- Order summary -------------------------------------------------------------------

  if (stage === 'summary' && order) {
    return (
      <div className="sf-card sf-card-pad">
        <h3 className="sf-section-title">Confirm your order</h3>
        <InlineError message={error} />

        <dl className="sf-kv mb-3">
          <div>
            <dt>Service</dt>
            <dd>{plan?.name ?? requirement.category}</dd>
          </div>
          <div>
            <dt>Applies to</dt>
            <dd>{propertyName}</dd>
          </div>
          <div>
            <dt>Base amount</dt>
            <dd>{money(order.amount)}</dd>
          </div>
          <div>
            <dt>Tax ({plan?.taxPercentage ?? requirement.taxPercentage}%)</dt>
            <dd>{money(order.taxAmount)}</dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>{plan?.durationDays ?? requirement.listingValidityDays} days</dd>
          </div>
          <div className="sf-kv__total">
            <dt>Total payable</dt>
            <dd>
              <strong>{money(order.totalAmount)}</strong>
            </dd>
          </div>
        </dl>

        {order.isSimulated && (
          <div className="sf-simulated-banner mb-3" role="note">
            <strong>Development test payment.</strong> Confirming below records a simulated
            payment and activates the service for testing. No real money will be charged.
          </div>
        )}
        {!order.isSimulated && order.provider === 'Razorpay' && (
          <div className="sf-simulated-banner mb-3" role="note">
            <strong>Razorpay Test Mode.</strong> This is a sandbox payment; no real money will be charged.
          </div>
        )}

        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={order.isSimulated ? confirmSimulated : startRazorpayCheckout}
            disabled={busy || (order.isSimulated ? !canSimulate : !canUseRazorpay)}
          >
            {busy
              ? (order.isSimulated ? 'Confirming…' : 'Opening secure checkout…')
              : order.isSimulated
                ? `Confirm and pay ${money(order.totalAmount)}`
                : `Pay with Razorpay Test Mode ${money(order.totalAmount)}`}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              setOrder(null);
              setStage('select');
            }}
            disabled={busy}
          >
            Back
          </button>
        </div>

        {!order.isSimulated && !canUseRazorpay && (
          <p className="sf-muted small mb-0 mt-2">
            This order cannot be completed because the server has not enabled a matching payment method.
          </p>
        )}
      </div>
    );
  }

  // --- Plan and payment option ---------------------------------------------------------

  return (
    <div className="sf-card sf-card-pad">
      <h3 className="sf-section-title">Choose a plan</h3>
      <InlineError message={error} />

      <div className="sf-planlist mb-3">
        {plans.map((p) => (
          <label key={p.id} className={`sf-plan${planId === p.id ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="plan"
              value={p.id}
              checked={planId === p.id}
              onChange={() => setPlanId(p.id)}
            />
            <span className="sf-plan__body">
              <span className="sf-plan__name">{p.name}</span>
              <span className="sf-plan__price">
                {money(p.totalAmount)}
                <span className="sf-muted"> / {p.durationDays} days</span>
              </span>
              {p.features.length > 0 && (
                <span className="sf-plan__features">
                  {p.features.map((f) => (
                    <span key={f}>
                      <IconCheck aria-hidden="true" />
                      {f}
                    </span>
                  ))}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>

      <h3 className="sf-section-title">Payment method</h3>

      {canSimulate ? (
        <div className="sf-paymethod is-selected">
          <IconAlert className="sf-paymethod__icon" />
          <span>
            <strong>Development Test Payment</strong>
            <span className="sf-muted d-block small">
              No real money will be charged. Recorded as a simulated payment.
            </span>
          </span>
        </div>
      ) : requirement.gatewayIntegrated ? (
        <div className="sf-paymethod is-selected">
          <IconCard className="sf-paymethod__icon" />
          <span>
            <strong>Razorpay Test Mode</strong>
            <span className="sf-muted d-block small">
              Secure sandbox checkout. No real money will be charged.
            </span>
          </span>
        </div>
      ) : (
        <div className="sf-paymethod">
          <IconClock className="sf-paymethod__icon" />
          <span>
            <strong>No payment method available</strong>
            <span className="sf-muted d-block small">{requirement.message}</span>
          </span>
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={createOrder}
          disabled={busy || !planId}
        >
          <IconCard /> {busy ? 'Preparing…' : 'Proceed to pay'}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-link" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
